
import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../AdminDashboard.css';
import SUPPayoutReport from './SUPPayoutReport';
import SUPJeopardyReport from './SUPJeopardyReport';
import SUPPullListReport from './SUPPullListReport';
import '../Buttons.css';
import api from '../../api';

const starterSizes = [
    { description: "4 inch pot", packetSize: 1 },
    { description: "6-pack", packetSize: 6 },
    { description: "4-pack", packetSize: 4 }
];


// Define the sections you want to manage
const sections = [

    {
        key: 'supplierUsers',
        label: 'Users',
        endpoint: 'supplierUsers/admin',
        columns: ['name', 'role', 'mustResetPassword', 'isActive'],
        allowAdd: false,
        allowEdit: true,
        allowDelete: false
    },

    {
        key: 'announcements',
        label: 'Announcements',
        endpoint: '/announcements/supplierAdmin',
        columns: ['title', 'description', 'startDate', 'endDate', 'isActive'],
        allowAdd: true,
        allowEdit: true,
        allowDelete: false,
        detail: {
            detailEndpoint: '/announcements/supplierAdmin'
        }
    },
    {
        key: 'seasonalTips',
        label: 'Seasonal Tips',
        endpoint: '/seasonalTips/supplierAdmin',
        columns: ['title', 'description', 'startDate', 'endDate', 'isActive'],
        allowAdd: true,
        allowEdit: true,
        allowDelete: false,
        detail: {
            detailEndpoint: '/seasonalTips/supplierAdmin'
        }

    },
    {
        key: 'catchments',
        label: 'Catchments',
        endpoint: '/locations/supplierAdmin',
        columns: ['city', 'delivery'],
        allowAdd: true,
        allowEdit: true,
        allowDelete: true
    },
    {
        key: 'starterPrice',
        label: 'Starter Price',
        endpoint: '/supplierPrice',
        columns: ['description', 'price'],
        allowAdd: true,
        allowEdit: true,
        allowDelete: true
    },
    // add more as needed
];

const GenericDetailTableSection = ({ masterId, detail }) => {
    const [allDetails, setAllDetails] = useState([]);
    const [linkedDetails, setLinkedDetails] = useState([]);

    // Fetch all possible details
    useEffect(() => {
        if (!detail || !masterId) return;
        const fetchAll = async () => {
            try {
                const res = await api.get(`${detail.detailEndpoint}/${masterId}/cities/all`);
                setAllDetails(res.data || []);
            } catch (err) {
                console.error('Error fetching all details:', err);
            }
        };
        fetchAll();
    }, [detail, masterId]);

    // Fetch linked details
    useEffect(() => {
        if (!detail || !masterId) return;
        const fetchLinked = async () => {
            try {
                const res = await api.get(`${detail.detailEndpoint}/${masterId}/cities`);
                setLinkedDetails(res.data || []);
            } catch (err) {
                console.error('Error fetching linked details:', err);
            }
        };
        fetchLinked();
    }, [detail, masterId]);

    const handleLinkUnlink = async (detailId, checked) => {
        try {
            if (checked) {
                await api.post(`${detail.detailEndpoint}/${masterId}/cities/link`, { detailId });
            } else {
                await api.delete(`${detail.detailEndpoint}/${masterId}/cities/unlink/${detailId}`);
            }
            const res = await api.get(`${detail.detailEndpoint}/${masterId}/cities`);
            setLinkedDetails(res.data || []);
        } catch (err) {
            console.error('Error updating detail link:', err);
        }
    };

    if (!masterId) return null;

    return (
        <div className="detail-list">
            <h5>Linked Cities</h5>
            <ul>
                {allDetails.map(d => {
                    const isLinked = linkedDetails.some(ld => ld.id === d.id);
                    return (
                        <li key={d.id}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={isLinked}
                                    onChange={(e) => handleLinkUnlink(d.id, e.target.checked)}
                                />
                                {d.name}
                            </label>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const GenericTableSection = ({ section, currentSupplier, adminIds }) => {
    const { endpoint, columns, label, allowAdd, allowEdit, allowDelete } = section;
    const [rows, setRows] = useState([]);
    const [dirtyCells, setDirtyCells] = useState({});
    const [status, setStatus] = useState('');
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [allCities, setAllCities] = useState([]);

    useEffect(() => {
        if (section.key === 'catchments') {
            api.get('/locations/all').then(res => setAllCities(res.data));
        }
    }, [section.key]);


    const fetchRows = async () => {
        try {
            const res = await api.get(endpoint);
            setRows(res.data);
            setDirtyCells({});
        } catch (err) {
            console.error(`Error fetching ${endpoint}:`, err);
        }
    };

    useEffect(() => {
        fetchRows();
    }, [endpoint, section]);

    const handleCellChange = (rowID, col, value) => {
        setRows(prev =>
            prev.map(row =>
                row.id === rowID ? { ...row, [col]: value } : row
            )
        );
        setDirtyCells(prev => ({ ...prev, [`${rowID}|${col}`]: true }));
    };

    const handleAddRow = () => {

        const newRow = {
            id: Date.now(),
            isTemporary: true,
            isEditing: true,
            createdBy: currentSupplier.userid
        };
        columns.forEach(col => {
            newRow[col] = col === 'isActive' ? false : '';
        });

        if (section.key === 'starterPrice') {
            newRow.description = "";
            newRow.packetSize = "";   // hidden but required for backend
        }

        setRows(prev => [...prev, newRow]);
        setDirtyCells(prev => ({ ...prev, [`${newRow.id}|new`]: true }));
    };

    const handleSubmitChanges = async () => {
        const updates = rows.filter(r =>
            Object.keys(dirtyCells).some(key => key.startsWith(`${r.id}|`))
        );

        if (updates.length === 0) {
            setStatus('No changes to submit.');
            return;
        }

        try {
            for (const row of updates) {

                // SPECIAL CASE: catchments no generic validation here
                if (section.key === 'catchments') {
                    const cityObj = allCities.find(c => c.city === row.city);
                    if (!cityObj) continue;

                    const payload = {
                        city: cityObj.id,
                        delivery: row.delivery === '' ? null : Number(row.delivery)
                    };

                    if (row.isTemporary) {
                        await api.post(endpoint, payload);
                    } else {
                        await api.put(`${endpoint}/${row.id}`, payload);
                    }

                    continue; // skip generic handling entirely
                }

                // GENERIC VALIDATION (only for non catchments)
                for (const col of columns) {
                    const value = row[col];

                    if (value === null || value === undefined || value === '') {
                        setStatus('Please fill in all fields before submitting.');
                        return;
                    }

                    if (col.toLowerCase().includes('date')) {
                        const d = new Date(value);
                        if (isNaN(d.getTime())) {
                            setStatus('Please enter valid dates before submitting.');
                            return;
                        }
                    }
                }

                // GENERIC PAYLOAD
                const payload = {};
                columns.forEach(col => {
                    payload[col] = row[col];
                });
                
                if (section.key === 'starterPrice') {
                    payload.packetSize = row.packetSize;   // include hidden internal value
                }


                if (row.isTemporary) {
                    await api.post(endpoint, payload);
                } else {
                    await api.put(`${endpoint}/${row.id}`, payload);
                }
            }

            setStatus('Changes submitted.');
            setDirtyCells({});
            fetchRows();

        } catch (err) {
            console.error('Error submitting changes:', err);
            setStatus('Error submitting changes.');
        }
    };

const handleDelete = async (id) => {
    try {
        await api.delete(`${endpoint}/${id}`);
        setStatus('Row deleted.');
        fetchRows();
    } catch (err) {
        console.error(`Error deleting row from ${endpoint}:`, err);

        // Show backend message if available
        const backendMessage =
            err.response?.data?.error ||
            err.response?.data?.details ||
            'Error deleting row.';

        setStatus(backendMessage);
    }
};

    const renderInput = (rowID, col, value) => {
        const row = rows.find(r => r.id === rowID);
        console.log('inside renderInput currentSupplier: ', currentSupplier);

        // Row-level permission check 
        const canEdit =
            adminIds.includes(row.createdBy) || // created by ANY gardener admin 
            row.createdBy === currentSupplier.userid; // created by this supplier

        // Apply edit permissions ONLY to announcements and seasonalTips
        if ((section.key === 'announcements' || section.key === 'seasonalTips')) {
            if (!allowEdit || !canEdit) {
                return <span>{value}</span>;
            }
        }

        // SPECIAL CASE: catchments city dropdown 
        if (section.key === 'catchments' && col === 'city') {

            // If this is an existing row, show read-only city name
            if (!row.isTemporary) {
                return <span>{value}</span>; // value is the city name
            }

            // Compute available cities by ID 
            const usedCityIds = rows
                .filter(r => !r.isTemporary)
                .map(r => r.city);

            const availableCities = allCities.filter(c => !usedCityIds.includes(c.city));

            // Otherwise show dropdown for new rows
            return (
                <select
                    value={value || ''}
                    onChange={(e) => handleCellChange(rowID, 'city', e.target.value)}
                >
                    <option value="">Select a city</option>
                    {availableCities.map(city => (
                        <option key={city.id} value={city.city}>
                            {city.city}
                        </option>
                    ))}
                </select>
            );
        }

        if (col === 'isActive') {
            return (
                <input
                    type="checkbox"
                    checked={!!value}
                    onChange={(e) => handleCellChange(rowID, col, e.target.checked)}
                />
            );
        }
        if (col.toLowerCase().includes('date')) {
            return (
                <DatePicker
                    selected={value ? new Date(value) : null}
                    onChange={(date) => {
                        const isoDate = date.toISOString().split('T')[0];
                        handleCellChange(rowID, col, isoDate);
                    }}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select date"
                    className="calendar-input"
                    required
                />
            );
        }

// SPECIAL CASE: Starter Price dropdown for description
if (section.key === 'starterPrice' && col === 'description') {

    // 1. Find which descriptions are already used (except this row)
    const usedDescriptions = rows
        .filter(r => r.id !== rowID)        // ignore current row
        .map(r => r.description)
        .filter(Boolean);

    // 2. Filter out used sizes so supplier cannot pick duplicates
    const availableSizes = starterSizes.filter(
        s => !usedDescriptions.includes(s.description)
    );

    return (
        <select
            value={value || ''}
            onChange={(e) => {
                const selected = starterSizes.find(
                    s => s.description === e.target.value
                );
                handleCellChange(rowID, 'description', selected.description);
                handleCellChange(rowID, 'packetSize', selected.packetSize); // hidden internal value
            }}
        >
            <option value="">Select size...</option>

            {availableSizes.map(s => (
                <option key={s.packetSize} value={s.description}>
                    {s.description}
                </option>
            ))}
        </select>
    );
}

        return (
            <input
                type="text"
                value={value || ''}
                onChange={(e) => handleCellChange(rowID, col, e.target.value)}
            />
        );
    };

    return (
        <div className="generic-table-section">
            <h4>Currently Editing: {label}</h4>
            <div className="matrix-grid">
                <div className="matrix-columns header-row">
                    {columns.map(col => (
                        <div key={col} className="matrix-column">{col}</div>
                    ))}
                    {allowDelete && <div className="matrix-column">Actions</div>}
                </div>

                {rows.map(row => (
                    <div
                        key={row.id}
                        className={`matrix-columns data-row ${selectedRowId === row.id ? 'selected' : ''}`}
                        onClick={() => setSelectedRowId(row.id)}
                    >
                        {columns.map(col => (
                            <div key={col} className="matrix-column">
                                {renderInput(row.id, col, row[col])}
                            </div>
                        ))}

                        <div className="matrix-column">
                            {allowDelete && (
                                <button
                                    onClick={() => handleDelete(row.id)}
                                    className="button small danger"
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {section.detail && selectedRowId && rows.some(r => r.id === selectedRowId && !r.isTemporary) && (
                <GenericDetailTableSection
                    masterId={selectedRowId}
                    detail={section.detail}
                />
            )}

            <div className="table-actions">
                {allowAdd && <button onClick={handleAddRow} className="button">Add Row</button>}
                {allowEdit && <button onClick={handleSubmitChanges} className="button">Submit Changes</button>}
                {status && <p className="status-message">{status}</p>}
            </div>
        </div>
    );
};


const SUPAdminDashboard = ({ currentSupplier, token, handleTabChange }) => {
    const [selectedSection, setSelectedSection] = useState(sections[0]);
    const [status, setStatus] = useState('');
    const [adminIds, setAdminIds] = useState([]);
    const [showCreateUserForm, setShowCreateUserForm] = useState(false);

    const [staffName, setStaffName] = useState('');
    const [staffPassword, setStaffPassword] = useState('');
    const [staffRole, setStaffRole] = useState('CASHIER');
    const [showPayoutModal, setShowPayoutModal] = useState(false);
    const [showJeopardyModal, setShowJeopardyModal] = useState(false);
    const [showPullListModal, setShowPullListModal] = useState(false);

    useEffect(() => {
        api.get('/users/gardenerAdmins').then(res => {
            setAdminIds(res.data);
        });
    }, []);

    const handleCreateSupplierUser = () => {
        api.post('/auth/registerSupplierUser', {
            name: staffName,
            password: staffPassword,
            role: (staffRole || 'CASHIER').toUpperCase(),
            supplierID: currentSupplier.id
        })
            .then(res => {
                setStatus(res.data.message || 'Supplier user created');

                setStaffName('');
                setStaffPassword('');
                setStaffRole('');
                setShowCreateUserForm(false);
                setSelectedSection({ ...sections[0] });

            })
            .catch((err) => {
                const backendMessage =
                    err.response?.data?.error ||
                    err.response?.data?.details ||
                    'Error creating supplier user';

                setStatus(backendMessage);
            });
    };


    return (
        <div className="admin-dashboard">

            <div className="top-actions">
                <h3>Manage Users</h3>

                <button className="button" onClick={() => setShowCreateUserForm(true)}>
                    Create a New User
                </button>

                {status && <p className="status-message">{status}</p>}
            </div>

            {showCreateUserForm && (
                <div className="supplier-form">
                    <h4>Create a New User</h4>

                    <label>Name</label>
                    <input
                        type="text"
                        value={staffName}
                        onChange={(e) => setStaffName(e.target.value)}
                    />

                    <label>Temporary Password</label>
                    <input
                        type="text"
                        value={staffPassword}
                        onChange={(e) => setStaffPassword(e.target.value)}
                    />

                    <label>Role</label>
                    <select
                        value={staffRole}
                        onChange={(e) => setStaffRole(e.target.value)}
                    >
                        <option value="admin">Admin</option>
                        <option value="cashier">Cashier</option>
                        <option value="buyer">Buyer</option>
                    </select>

                    <button className="button" onClick={handleCreateSupplierUser}>
                        Create User
                    </button>

                    <button
                        className="button secondary"
                        onClick={() => setShowCreateUserForm(false)}
                    >
                        Cancel
                    </button>
                </div>
            )}

            <div className="top-actions">
                <h3>Manage Reports</h3>

                <button
                    className="button"
                    onClick={() => setShowPayoutModal(true)}
                >
                    View Payout Report
                </button>

                <button
                    className="button"
                    onClick={() => setShowJeopardyModal(true)}
                    style={{ marginLeft: '10px' }}
                >
                    View Order Jeopardy Report
                </button>

                <button
                    className="button"
                    onClick={() => setShowPullListModal(true)}
                    style={{ marginLeft: '10px' }}
                >
                    View Pull Sheet
                </button>

            </div>

            {showPayoutModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button
                            className="button"
                            onClick={() => setShowPayoutModal(false)}
                        >
                            Close
                        </button>

                        <SUPPayoutReport currentSupplier={currentSupplier} />
                    </div>
                </div>
            )}

            {showJeopardyModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button
                            className="button"
                            onClick={() => setShowJeopardyModal(false)}
                        >
                            Close
                        </button>

                        <SUPJeopardyReport currentSupplier={currentSupplier} />
                    </div>
                </div>
            )}

            {showPullListModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button
                            className="button"
                            onClick={() => setShowPullListModal(false)}
                        >
                            Close
                        </button>

                        <SUPPullListReport currentSupplier={currentSupplier} />
                    </div>
                </div>
            )}


            {/* Existing lookup-table section */}
            <section>
                <h3>Manage Lookup Tables</h3>

                <nav className="admin-menu">
                    {sections.map(section => (
                        <button
                            key={section.key}
                            className={selectedSection?.key === section.key ? 'active' : ''}
                            onClick={() => setSelectedSection(section)}
                        >
                            {section.label}
                        </button>
                    ))}
                </nav>
                <GenericTableSection
                        section={selectedSection}
                        currentSupplier={currentSupplier}
                        adminIds={adminIds}
                    />
            </section>
        </div>
    );
};

export default SUPAdminDashboard;
